import { useState, useMemo, useCallback, useRef, useEffect, Fragment } from "react";
import { useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Save, RotateCcw, CheckCircle2, XCircle, Shield,
  LayoutDashboard, Users, PiggyBank, HandCoins, Receipt,
  Briefcase, BarChart3, Bot, Settings, UserCog,
  ChevronDown, ChevronRight, CheckCheck, Minus, LoaderCircle,
  AlertTriangle, Filter, Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// ─── Types ────────────────────────────────────────────────────────────

interface Role {
  id: number;
  name: string;
  code?: string;
  status: string;
  permissions?: Record<string, boolean>;
}

interface ModuleDef {
  key: string;
  label: string;
}

type SelectionMap = Set<string>;

// ─── Constants ────────────────────────────────────────────────────────

const MODULES: ModuleDef[] = [
  { key: "dashboard", label: "Dashboard" },
  { key: "members", label: "Member Management" },
  { key: "contributions", label: "Savings & Contributions" },
  { key: "loans", label: "Loans & Payments" },
  { key: "expenses", label: "Expenses & Bills" },
  { key: "meetings", label: "Meetings & Attendance" },
  { key: "reports", label: "Reports & Analytics" },
  { key: "payroll", label: "HR Payroll" },
  { key: "ai", label: "AI Assistant" },
  { key: "settings", label: "System Settings" },
  { key: "users", label: "Users & Roles" },
  { key: "portal", label: "Member Portal" },
];

const MODULE_ACTIONS: Record<string, string[]> = {
  dashboard: ["view", "analytics"],
  members: ["view", "create", "edit", "delete", "approve", "export"],
  contributions: ["view", "create", "edit", "delete", "verify", "export"],
  loans: ["view", "create", "edit", "delete", "approve", "reject", "verify", "export"],
  expenses: ["view", "create", "edit", "delete", "approve", "export"],
  meetings: ["view", "create", "edit", "delete", "export"],
  reports: ["view", "export", "print"],
  payroll: ["view", "create", "edit", "delete", "export", "import"],
  ai: ["view"],
  settings: ["view", "edit", "manage"],
  users: ["view", "create", "edit", "delete", "manage"],
  portal: ["view"],
};

const ACTION_LABELS: Record<string, string> = {
  view: "View", analytics: "Analytics",
  create: "Create", edit: "Edit", delete: "Delete",
  approve: "Approve", reject: "Reject", verify: "Verify",
  export: "Export", print: "Print", import: "Import", manage: "Manage",
};

const MODULE_ICONS: Record<string, any> = {
  dashboard: LayoutDashboard, members: Users, contributions: PiggyBank,
  loans: HandCoins, expenses: Receipt, meetings: Users,
  reports: BarChart3, payroll: Briefcase, ai: Bot,
  settings: Settings, users: UserCog, portal: Users,
};

const MODULE_COLORS: Record<string, string> = {
  dashboard: "bg-blue-50 text-blue-700 border-blue-200",
  members: "bg-emerald-50 text-emerald-700 border-emerald-200",
  contributions: "bg-violet-50 text-violet-700 border-violet-200",
  loans: "bg-amber-50 text-amber-700 border-amber-200",
  expenses: "bg-rose-50 text-rose-700 border-rose-200",
  meetings: "bg-cyan-50 text-cyan-700 border-cyan-200",
  reports: "bg-indigo-50 text-indigo-700 border-indigo-200",
  payroll: "bg-orange-50 text-orange-700 border-orange-200",
  ai: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  settings: "bg-slate-50 text-slate-700 border-slate-200",
  users: "bg-sky-50 text-sky-700 border-sky-200",
  portal: "bg-teal-50 text-teal-700 border-teal-200",
};

const ROW_COLORS: Record<string, string> = {
  dashboard: "hover:bg-blue-50/30",
  members: "hover:bg-emerald-50/30",
  contributions: "hover:bg-violet-50/30",
  loans: "hover:bg-amber-50/30",
  expenses: "hover:bg-rose-50/30",
  meetings: "hover:bg-cyan-50/30",
  reports: "hover:bg-indigo-50/30",
  payroll: "hover:bg-orange-50/30",
  ai: "hover:bg-fuchsia-50/30",
  settings: "hover:bg-slate-50/30",
  users: "hover:bg-sky-50/30",
  portal: "hover:bg-teal-50/30",
};

function buildKey(module: string, action: string) {
  return `${module}.${action}`;
}

function cellId(roleId: number, permissionKey: string) {
  return `${roleId}:${permissionKey}`;
}

function parseCellId(id: string): { roleId: number; permissionKey: string } {
  const colon = id.indexOf(":");
  return { roleId: Number(id.slice(0, colon)), permissionKey: id.slice(colon + 1) };
}

// ─── Permission Cell Component ────────────────────────────────────────

function PermissionCell({
  granted,
  selected,
  onMouseDown,
  onMouseEnter,
  animated = false,
}: {
  granted: boolean;
  selected: boolean;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  animated?: boolean;
}) {
  const state = selected ? "selected" : granted ? "granted" : "none";

  return (
    <button
      type="button"
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      className={`
        relative flex items-center justify-center
        w-8 h-8 rounded-lg
        transition-all duration-150 cursor-pointer
        outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1
        ${state === "selected"
          ? "bg-indigo-100 text-indigo-600 ring-2 ring-indigo-400 scale-110 z-10"
          : state === "granted"
            ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:shadow-sm ring-1 ring-emerald-200"
            : "bg-slate-50 text-slate-300 hover:bg-slate-100 hover:text-slate-400 ring-1 ring-slate-200"
        }
      `}
      title={granted ? "Granted – click to toggle" : "Not granted – click to toggle"}
    >
      {animated ? (
        <motion.div
          key={`${granted}-${selected}`}
          initial={false}
          animate={state === "selected" ? { scale: 1 } : granted ? { scale: 1 } : { scale: 0.85 }}
          className={`
            w-4 h-4 rounded border-2 flex items-center justify-center
            transition-colors duration-150
            ${state === "selected"
              ? "bg-indigo-600 border-indigo-600"
              : state === "granted"
                ? "bg-emerald-500 border-emerald-500"
                : "border-slate-300 bg-white"
            }
          `}
        >
          {state === "granted" && (
            <motion.svg
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.2 }}
              viewBox="0 0 24 24"
              fill="none"
              className="h-3 w-3 text-white"
            >
              <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
            </motion.svg>
          )}
          {state === "selected" && <Minus className="h-3 w-3 text-white" />}
        </motion.div>
      ) : (
        <div
          className={`
            w-4 h-4 rounded border-2 flex items-center justify-center transition-colors
            ${state === "selected"
              ? "bg-indigo-600 border-indigo-600"
              : state === "granted"
                ? "bg-emerald-500 border-emerald-500"
                : "border-slate-300 bg-white"
            }
          `}
        >
          {state === "granted" && <CheckCircle2 className="h-3 w-3 text-white" />}
          {state === "selected" && <Minus className="h-3 w-3 text-white" />}
        </div>
      )}
    </button>
  );
}

// ─── Module Row Checkbox (tri-state) ─────────────────────────────────

function ModuleRowCheckbox({
  roleId,
  moduleKey,
  actions,
  permissions,
  onToggle,
}: {
  roleId: number;
  moduleKey: string;
  actions: string[];
  permissions: Record<string, boolean>;
  onToggle: (value: boolean) => void;
}) {
  const grantedCount = actions.filter(a => permissions[buildKey(moduleKey, a)]).length;
  const total = actions.length;
  const state = grantedCount === 0 ? "empty" : grantedCount === total ? "full" : "partial";

  return (
    <button
      type="button"
      onClick={() => onToggle(state !== "full")}
      className={`
        flex items-center justify-center w-7 h-7 rounded-md transition-all cursor-pointer
        focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1
        ${state === "full"
          ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
          : state === "partial"
            ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
            : "bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
        }
      `}
      title={`${grantedCount}/${total} granted`}
    >
      {state === "full" && <CheckCircle2 className="h-3.5 w-3.5" />}
      {state === "partial" && <Minus className="h-3.5 w-3.5" />}
      {state === "empty" && <div className="h-3.5 w-3.5 rounded-sm border-2 border-slate-300" />}
    </button>
  );
}

// ─── Role Column Header ───────────────────────────────────────────────

function RoleColumnHeader({
  role,
  totalPerms,
  grantedCount,
  allGranted,
  onToggleAll,
  onSelectRole,
  isSelected,
}: {
  role: Role;
  totalPerms: number;
  grantedCount: number;
  allGranted: boolean;
  onToggleAll: () => void;
  onSelectRole: () => void;
  isSelected: boolean;
}) {
  const pct = totalPerms > 0 ? Math.round((grantedCount / totalPerms) * 100) : 0;

  return (
    <div className="flex flex-col items-center gap-1.5 px-2 py-2 min-w-[90px]">
      <button
        type="button"
        onClick={onSelectRole}
        className={`text-[11px] font-semibold truncate max-w-[80px] text-center leading-tight transition-colors cursor-pointer hover:text-indigo-600 ${isSelected ? "text-indigo-600 underline" : "text-slate-700"}`}
        title="Click to filter by this role"
      >
        {role.name}
      </button>
      <button
        type="button"
        onClick={onToggleAll}
        className={`
          flex items-center justify-center w-6 h-6 rounded-md transition-all cursor-pointer
          focus-visible:ring-2 focus-visible:ring-indigo-400
          ${allGranted
            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
            : grantedCount > 0
              ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
              : "bg-slate-100 text-slate-400 hover:bg-slate-200"
          }
        `}
        title={allGranted ? "Revoke all" : "Grant all"}
      >
        {allGranted ? (
          <CheckCircle2 className="h-3.5 w-3.5" />
        ) : grantedCount > 0 ? (
          <Minus className="h-3.5 w-3.5" />
        ) : (
          <div className="h-3.5 w-3.5 rounded-sm border-2 border-slate-300" />
        )}
      </button>
      <div className="text-[10px] font-mono text-slate-400">
        {grantedCount}/{totalPerms}
      </div>
      <div className="w-full h-1 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${pct}%`,
            backgroundColor: pct === 100 ? "#10B981" : pct > 50 ? "#F59E0B" : "#CBD5E1",
          }}
        />
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

interface PermissionsMatrixProps {
  roles: Role[];
  initialRoleId?: number;
}

export default function PermissionsMatrix({ roles, initialRoleId }: PermissionsMatrixProps) {
  const queryClient = useQueryClient();
  const tableRef = useRef<HTMLDivElement>(null);

  // ── State ──────────────────────────────────────────────────────────
  const [localPerms, setLocalPerms] = useState<Record<number, Record<string, boolean>>>({});
  const [originalPerms, setOriginalPerms] = useState<Record<number, Record<string, boolean>>>({});
  const [selectedCells, setSelectedCells] = useState<SelectionMap>(new Set());
  const [selectionAnchor, setSelectionAnchor] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [showGrantedOnly, setShowGrantedOnly] = useState(false);
  const [showUngrantedOnly, setShowUngrantedOnly] = useState(false);
  const [collapseAll, setCollapseAll] = useState(false);
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});
  const [confirmGrantAll, setConfirmGrantAll] = useState(false);
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<number | "all">(initialRoleId ?? "all");

  useEffect(() => {
    if (initialRoleId !== undefined) {
      setSelectedRoleId(initialRoleId);
    }
  }, [initialRoleId]);

  const activeRoles = useMemo(() => {
    const filtered = roles.filter(r => r.status === "active");
    if (selectedRoleId === "all") return filtered;
    return filtered.filter(r => r.id === selectedRoleId);
  }, [roles, selectedRoleId]);

  // ── Initialize ─────────────────────────────────────────────────────
  useEffect(() => {
    if (roles.length > 0) {
      const perms: Record<number, Record<string, boolean>> = {};
      for (const role of roles) {
        perms[role.id] = { ...(role.permissions || {}) };
      }
      setLocalPerms(perms);
      setOriginalPerms(JSON.parse(JSON.stringify(perms)));
      setSelectedCells(new Set());
      setCollapseAll(false);
      const expanded: Record<string, boolean> = {};
      MODULES.forEach(m => { expanded[m.key] = true; });
      setExpandedModules(expanded);
    }
  }, [roles]);

  // ── Derived State ──────────────────────────────────────────────────
  const allPermissionKeys = useMemo(() => {
    const keys: string[] = [];
    for (const mod of MODULES) {
      const actions = MODULE_ACTIONS[mod.key] || [];
      for (const action of actions) {
        keys.push(buildKey(mod.key, action));
      }
    }
    return keys;
  }, []);

  const totalPermCount = allPermissionKeys.length;

  const hasChanges = useMemo(() => {
    return JSON.stringify(localPerms) !== JSON.stringify(originalPerms);
  }, [localPerms, originalPerms]);

  const totalChanges = useMemo(() => {
    let count = 0;
    for (const roleIdStr of Object.keys(localPerms)) {
      const roleId = Number(roleIdStr);
      const current = localPerms[roleId] || {};
      const orig = originalPerms[roleId] || {};
      for (const key of allPermissionKeys) {
        if ((current[key] || false) !== (orig[key] || false)) count++;
      }
    }
    return count;
  }, [localPerms, originalPerms, allPermissionKeys]);

  const filteredModules = useMemo(() => {
    return MODULES.filter(mod => {
      if (moduleFilter !== "all" && moduleFilter !== mod.key) return false;
      const actions = MODULE_ACTIONS[mod.key] || [];
      if (actionFilter !== "all" && !actions.includes(actionFilter)) return false;
      if (searchTerm) {
        const matchesMod = mod.label.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesAction = actions.some(a => a.toLowerCase().includes(searchTerm.toLowerCase()));
        if (!matchesMod && !matchesAction) return false;
      }
      if (showGrantedOnly) {
        const anyGranted = activeRoles.some(role => {
          const perms = localPerms[role.id] || {};
          return actions.some(a => perms[buildKey(mod.key, a)]);
        });
        if (!anyGranted) return false;
      }
      if (showUngrantedOnly) {
        const allFull = activeRoles.every(role => {
          const perms = localPerms[role.id] || {};
          return actions.every(a => perms[buildKey(mod.key, a)]);
        });
        if (allFull) return false;
      }
      return true;
    });
  }, [moduleFilter, actionFilter, searchTerm, showGrantedOnly, showUngrantedOnly, activeRoles, localPerms]);

  const cellCountForRole = useCallback((roleId: number) => {
    const perms = localPerms[roleId] || {};
    return allPermissionKeys.filter(k => perms[k]).length;
  }, [localPerms, allPermissionKeys]);

  const moduleActions = useCallback((modKey: string) => {
    return MODULE_ACTIONS[modKey] || [];
  }, []);

  // ── Selection ──────────────────────────────────────────────────────
  const clearSelection = useCallback(() => {
    setSelectedCells(new Set());
    setSelectionAnchor(null);
  }, []);

  // ── Permission Toggles ─────────────────────────────────────────────
  const togglePerm = useCallback((roleId: number, permissionKey: string) => {
    setLocalPerms(prev => {
      const rolePerms = { ...(prev[roleId] || {}) };
      rolePerms[permissionKey] = !rolePerms[permissionKey];
      return { ...prev, [roleId]: rolePerms };
    });
  }, []);

  const handleCellMouseDown = useCallback((e: React.MouseEvent, roleId: number, permissionKey: string) => {
    const id = cellId(roleId, permissionKey);

    if (e.shiftKey && selectionAnchor) {
      e.preventDefault();
      const allIds: string[] = [];
      let foundAnchor = false;
      let foundCurrent = false;

      for (const mod of filteredModules) {
        const actions = MODULE_ACTIONS[mod.key] || [];
        for (const role of activeRoles) {
          for (const action of actions) {
            const cid = cellId(role.id, buildKey(mod.key, action));
            if (cid === selectionAnchor || cid === id) {
              if (foundAnchor) foundCurrent = true;
              else foundAnchor = true;
              if (foundAnchor && !foundCurrent) allIds.push(cid);
              if (foundAnchor && foundCurrent) { allIds.push(cid); break; }
            } else if (foundAnchor && !foundCurrent) {
              allIds.push(cid);
            }
          }
          if (foundAnchor && foundCurrent) break;
        }
        if (foundAnchor && foundCurrent) break;
      }

      setSelectedCells(prev => {
        const next = new Set(prev);
        const add = !next.has(id);
        for (const cid of allIds) {
          if (add) next.add(cid); else next.delete(cid);
        }
        return next;
      });
      return;
    }

    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      setSelectedCells(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
      if (!selectedCells.has(id)) setSelectionAnchor(id);
      return;
    }

    clearSelection();
    togglePerm(roleId, permissionKey);
  }, [selectionAnchor, filteredModules, activeRoles, selectedCells, clearSelection, togglePerm]);

  const handleCellMouseEnter = useCallback((roleId: number, permissionKey: string) => {
    if (isDragging) {
    }
  }, [isDragging]);

  const togglePermAdvanced = useCallback((e: React.MouseEvent, roleId: number, permissionKey: string) => {
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      handleCellMouseDown(e, roleId, permissionKey);
      return;
    }
    togglePerm(roleId, permissionKey);
  }, [handleCellMouseDown, togglePerm]);

  const batchSet = useCallback((value: boolean) => {
    if (selectedCells.size === 0) return;
    setLocalPerms(prev => {
      const next = { ...prev };
      for (const id of selectedCells) {
        const { roleId, permissionKey } = parseCellId(id);
        const rolePerms = { ...(next[roleId] || {}) };
        rolePerms[permissionKey] = value;
        next[roleId] = rolePerms;
      }
      return next;
    });
    clearSelection();
  }, [selectedCells, clearSelection]);

  const setModuleForRole = useCallback((roleId: number, modKey: string, value: boolean) => {
    setLocalPerms(prev => {
      const rolePerms = { ...(prev[roleId] || {}) };
      const actions = MODULE_ACTIONS[modKey] || [];
      for (const action of actions) {
        rolePerms[buildKey(modKey, action)] = value;
      }
      return { ...prev, [roleId]: rolePerms };
    });
  }, []);

  const setAllForRole = useCallback((roleId: number, value: boolean) => {
    setLocalPerms(prev => {
      const rolePerms: Record<string, boolean> = {};
      for (const mod of MODULES) {
        const actions = MODULE_ACTIONS[mod.key] || [];
        for (const action of actions) {
          rolePerms[buildKey(mod.key, action)] = value;
        }
      }
      return { ...prev, [roleId]: rolePerms };
    });
  }, []);

  const grantAll = useCallback(() => {
    setLocalPerms(prev => {
      const next = { ...prev };
      const roleIds = selectedRoleId === "all"
        ? Object.keys(next)
        : [String(selectedRoleId)];
      for (const roleIdStr of roleIds) {
        const roleId = Number(roleIdStr);
        const rolePerms: Record<string, boolean> = {};
        for (const mod of MODULES) {
          const actions = MODULE_ACTIONS[mod.key] || [];
          for (const action of actions) {
            rolePerms[buildKey(mod.key, action)] = true;
          }
        }
        next[roleId] = rolePerms;
      }
      return next;
    });
    setConfirmGrantAll(false);
  }, [selectedRoleId]);

  const revokeAll = useCallback(() => {
    setLocalPerms(prev => {
      const next = { ...prev };
      const roleIds = selectedRoleId === "all"
        ? Object.keys(next)
        : [String(selectedRoleId)];
      for (const roleIdStr of roleIds) {
        const roleId = Number(roleIdStr);
        const rolePerms: Record<string, boolean> = {};
        for (const mod of MODULES) {
          const actions = MODULE_ACTIONS[mod.key] || [];
          for (const action of actions) {
            rolePerms[buildKey(mod.key, action)] = false;
          }
        }
        next[roleId] = rolePerms;
      }
      return next;
    });
    setConfirmRevokeAll(false);
  }, [selectedRoleId]);

  // ── Save ───────────────────────────────────────────────────────────
  const saveAll = async () => {
    setSaving(true);
    try {
      const roleIds = Object.keys(localPerms);
      const toSave = roleIds.filter(id => {
        const rid = Number(id);
        return JSON.stringify(localPerms[rid]) !== JSON.stringify(originalPerms[rid]);
      });
      await Promise.all(toSave.map(async (id) => {
        await api.put(`/roles/${id}/permissions`, { permissions: localPerms[Number(id)] });
      }));
      toast.success(`Permissions updated for ${toSave.length} role(s)`);
      setOriginalPerms(JSON.parse(JSON.stringify(localPerms)));
      queryClient.invalidateQueries("roles");
      clearSelection();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = useCallback(() => {
    setLocalPerms(JSON.parse(JSON.stringify(originalPerms)));
    clearSelection();
  }, [originalPerms, clearSelection]);

  // ── Keyboard Shortcuts ─────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.ctrlKey && e.key === "a") {
        e.preventDefault();
        const allIds: string[] = [];
        for (const mod of filteredModules) {
          const actions = MODULE_ACTIONS[mod.key] || [];
          for (const role of activeRoles) {
            for (const action of actions) {
              allIds.push(cellId(role.id, buildKey(mod.key, action)));
            }
          }
        }
        setSelectedCells(new Set(allIds));
        return;
      }

      if (e.key === "Escape") {
        clearSelection();
        return;
      }

      if (e.key === " " && selectedCells.size > 0) {
        e.preventDefault();
        const first = selectedCells.values().next().value;
        if (first) {
          const { roleId, permissionKey } = parseCellId(first);
          togglePerm(roleId, permissionKey);
        }
        return;
      }

      if ((e.key === "Delete" || e.key === "Backspace") && selectedCells.size > 0) {
        e.preventDefault();
        batchSet(false);
        return;
      }

      if (e.ctrlKey && e.key === "s") {
        e.preventDefault();
        if (hasChanges) saveAll();
        return;
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [filteredModules, activeRoles, selectedCells, clearSelection, togglePerm, batchSet, hasChanges]);

  // ── Drag selection ─────────────────────────────────────────────────
  const handleMouseDownOnTable = useCallback((e: React.MouseEvent) => {
    if (e.shiftKey || e.ctrlKey || e.metaKey) return;
    if ((e.target as HTMLElement).closest("button")) return;
    setIsDragging(false);
  }, []);

  // ── Role-level grant counts ────────────────────────────────────────
  const roleCounts = useMemo(() => {
    const counts: Record<number, { granted: number; total: number; allGranted: boolean }> = {};
    for (const role of activeRoles) {
      const perms = localPerms[role.id] || {};
      const granted = allPermissionKeys.filter(k => perms[k]).length;
      counts[role.id] = { granted, total: allPermissionKeys.length, allGranted: granted === allPermissionKeys.length };
    }
    return counts;
  }, [activeRoles, localPerms, allPermissionKeys]);

  // ── First column sticky class ──────────────────────────────────────

  if (activeRoles.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Shield className="h-10 w-10 mx-auto mb-3 text-slate-300" />
        <p className="text-sm font-medium">No active roles to configure</p>
        <p className="text-xs mt-1">Activate roles first to manage permissions</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* ── Toolbar ──────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-[320px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search modules & actions..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
          <Select value={moduleFilter} onValueChange={setModuleFilter}>
            <SelectTrigger className="w-[140px] h-9 text-xs">
              <Filter className="h-3.5 w-3.5 mr-1" />
              <SelectValue placeholder="Module" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Modules</SelectItem>
              {MODULES.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[130px] h-9 text-xs">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {Object.entries(ACTION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(selectedRoleId)} onValueChange={(v) => setSelectedRoleId(v === "all" ? "all" : Number(v))}>
            <SelectTrigger className="w-[150px] h-9 text-xs">
              <UserCog className="h-3.5 w-3.5 mr-1" />
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {roles.filter(r => r.status === "active").map(r => (
                <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant={showGrantedOnly ? "default" : "outline"}
            size="sm"
            onClick={() => { setShowGrantedOnly(!showGrantedOnly); setShowUngrantedOnly(false); }}
            className={`h-9 text-xs ${showGrantedOnly ? "bg-emerald-600" : ""}`}
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
            Granted
          </Button>
          <Button
            variant={showUngrantedOnly ? "default" : "outline"}
            size="sm"
            onClick={() => { setShowUngrantedOnly(!showUngrantedOnly); setShowGrantedOnly(false); }}
            className={`h-9 text-xs ${showUngrantedOnly ? "bg-amber-600" : ""}`}
          >
            <XCircle className="h-3.5 w-3.5 mr-1" />
            Ungranted
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmGrantAll(true)}
            className="h-9 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <CheckCheck className="h-3.5 w-3.5 mr-1" />
            Grant All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmRevokeAll(true)}
            className="h-9 text-xs border-red-300 text-red-600 hover:bg-red-50"
          >
            <XCircle className="h-3.5 w-3.5 mr-1" />
            Revoke All
          </Button>
        </div>
      </div>

      {/* ── Stats Bar ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <Badge variant="outline" className="text-[11px] font-mono bg-slate-50 border-slate-200 text-slate-600">
          {filteredModules.length} modules
        </Badge>
        <Badge variant="outline" className="text-[11px] font-mono bg-slate-50 border-slate-200 text-slate-600">
          {activeRoles.length} role{activeRoles.length !== 1 ? "s" : ""}
        </Badge>
        <Badge variant="outline" className="text-[11px] font-mono bg-slate-50 border-slate-200 text-slate-600">
          {allPermissionKeys.length} permissions
        </Badge>
        {selectedCells.size > 0 && (
          <Badge variant="outline" className="text-[11px] font-mono bg-indigo-50 border-indigo-200 text-indigo-700">
            {selectedCells.size} selected
          </Badge>
        )}
        {hasChanges && (
          <Badge variant="outline" className="text-[11px] font-mono bg-amber-50 border-amber-200 text-amber-700">
            {totalChanges} unsaved
          </Badge>
        )}
      </div>

      {/* ── Table ───────────────────────────────────────────────────── */}
      <div
        ref={tableRef}
        className="overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm"
        onMouseDown={handleMouseDownOnTable}
      >
        <table className="w-full text-xs border-collapse">
          {/* ── Header ─────────────────────────────────────────────── */}
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="sticky left-0 z-20 bg-slate-50 px-3 py-2.5 text-left min-w-[160px] border-r border-slate-200">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Module / Action</span>
                  <span className="text-[10px] text-slate-400 font-mono">({allPermissionKeys.length})</span>
                </div>
              </th>
              {activeRoles.map(role => {
                const rc = roleCounts[role.id];
                return (
                  <th key={role.id} className="px-1 py-1 text-center border-r border-slate-100 last:border-r-0 min-w-[90px]">
                    <RoleColumnHeader
                      role={role}
                      totalPerms={rc?.total || 0}
                      grantedCount={rc?.granted || 0}
                      allGranted={rc?.allGranted || false}
                      onToggleAll={() => setAllForRole(role.id, !rc?.allGranted)}
                      onSelectRole={() => setSelectedRoleId(selectedRoleId === role.id ? "all" : role.id)}
                      isSelected={selectedRoleId === role.id}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* ── Body ───────────────────────────────────────────────── */}
          <tbody>
            {filteredModules.map(mod => {
              const actions = MODULE_ACTIONS[mod.key] || [];
              const Icon = MODULE_ICONS[mod.key] || Shield;
              const isExpanded = expandedModules[mod.key] !== false;
              const rowColor = ROW_COLORS[mod.key] || "hover:bg-slate-50/30";

              return (
                <Fragment key={mod.key}>
                  {/* Module header row */}
                  <tr className={`border-b border-slate-200 bg-slate-50/50 ${rowColor}`}>
                    <td className="sticky left-0 z-10 bg-inherit px-3 py-2 border-r border-slate-200">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setExpandedModules(prev => ({ ...prev, [mod.key]: !prev[mod.key] }))}
                          className="flex items-center justify-center w-5 h-5 rounded hover:bg-slate-200 transition-colors"
                        >
                          {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}
                        </button>
                        <div className={`flex items-center justify-center w-6 h-6 rounded-md ${MODULE_COLORS[mod.key] || "bg-slate-100"}`}>
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <span className="text-sm font-semibold text-slate-800">{mod.label}</span>
                        <span className="text-[10px] text-slate-400 font-mono">{actions.length}</span>
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            type="button"
                            onClick={() => {
                              for (const role of activeRoles) setModuleForRole(role.id, mod.key, true);
                            }}
                            className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-medium transition-colors"
                          >
                            Grant
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              for (const role of activeRoles) setModuleForRole(role.id, mod.key, false);
                            }}
                            className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 hover:bg-slate-200 font-medium transition-colors"
                          >
                            Revoke
                          </button>
                        </div>
                      </div>
                    </td>
                    {activeRoles.map(role => (
                      <td key={role.id} className="px-1 py-1.5 text-center border-r border-slate-100 last:border-r-0">
                        <ModuleRowCheckbox
                          roleId={role.id}
                          moduleKey={mod.key}
                          actions={actions}
                          permissions={localPerms[role.id] || {}}
                          onToggle={(value) => setModuleForRole(role.id, mod.key, value)}
                        />
                      </td>
                    ))}
                  </tr>

                  {/* Action rows */}
                  {isExpanded && actions.map((action, ai) => {
                    const actionKey = buildKey(mod.key, action);
                    return (
                      <tr
                        key={actionKey}
                        className={`border-b border-slate-100 transition-colors ${rowColor} ${ai % 2 === 0 ? "bg-white" : "bg-slate-50/20"}`}
                      >
                        <td className="sticky left-0 z-10 bg-inherit px-3 py-2 border-r border-slate-100">
                          <div className="flex items-center gap-2 pl-7">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0" />
                            <span className="text-xs font-medium text-slate-700">{ACTION_LABELS[action] || action}</span>
                          </div>
                        </td>
                        {activeRoles.map(role => {
                          const key = buildKey(mod.key, action);
                          const granted = localPerms[role.id]?.[key] || false;
                          const selId = cellId(role.id, key);
                          const selected = selectedCells.has(selId);
                          return (
                            <td key={`${role.id}-${key}`} className="px-1 py-1.5 text-center border-r border-slate-100 last:border-r-0">
                              <PermissionCell
                                granted={granted}
                                selected={selected}
                                onMouseDown={(e) => handleCellMouseDown(e, role.id, key)}
                                onMouseEnter={() => handleCellMouseEnter(role.id, key)}
                                animated
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
            {filteredModules.length === 0 && (
              <tr>
                <td colSpan={activeRoles.length + 1} className="px-4 py-10 text-center">
                  <div className="flex flex-col items-center gap-2 text-slate-400">
                    <Search className="h-6 w-6" />
                    <p className="text-sm font-medium">No modules match your filters</p>
                    <button
                      type="button"
                      onClick={() => { setSearchTerm(""); setModuleFilter("all"); setActionFilter("all"); setShowGrantedOnly(false); setShowUngrantedOnly(false); }}
                      className="text-xs text-indigo-500 hover:underline"
                    >
                      Clear all filters
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Perspective Summary Panel ────────────────────────────────── */}
      <AnimatePresence>
        {hasChanges && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="rounded-xl border border-amber-200 bg-amber-50/50 p-4"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-800">Unsaved Changes</p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    {totalChanges} permission{totalChanges !== 1 ? "s" : ""} modified across{" "}
                    {Object.keys(localPerms).filter(id => {
                      const rid = Number(id);
                      return JSON.stringify(localPerms[rid]) !== JSON.stringify(originalPerms[rid]);
                    }).length} role(s)
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={discardChanges}
                  className="h-8 text-xs border-amber-200 text-amber-700 hover:bg-amber-100"
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  Discard
                </Button>
                <Button
                  size="sm"
                  className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={saving}
                  onClick={saveAll}
                >
                  {saving ? (
                    <LoaderCircle className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Save Changes
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Bulk Actions Toolbar ─────────────────────────────────────── */}
      <AnimatePresence>
        {selectedCells.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="sticky bottom-4 z-50 flex items-center justify-between gap-4 rounded-xl border border-indigo-200 bg-white px-5 py-3 shadow-lg shadow-indigo-200/20"
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100">
                <Layers className="h-4 w-4 text-indigo-600" />
              </div>
              <div>
                <span className="text-sm font-semibold text-indigo-800">
                  {selectedCells.size} permission{selectedCells.size !== 1 ? "s" : ""} selected
                </span>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="ml-2 text-xs text-slate-400 hover:text-slate-600 underline"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => batchSet(true)}
                className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                Grant Selected
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => batchSet(false)}
                className="h-8 text-xs border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                <XCircle className="h-3.5 w-3.5 mr-1.5" />
                Revoke Selected
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Legacy-style bottom actions (always visible) ─────────────── */}
      {hasChanges && selectedCells.size === 0 && (
        <div className="sticky bottom-4 z-50 flex items-center justify-end gap-2 rounded-xl border border-amber-200 bg-white px-5 py-3 shadow-lg">
          <span className="text-xs text-amber-600 font-medium mr-auto">
            {totalChanges} unsaved change{totalChanges !== 1 ? "s" : ""}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={discardChanges}
            className="h-8 text-xs"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            Discard
          </Button>
          <Button
            size="sm"
            onClick={saveAll}
            disabled={saving}
            className="h-8 text-xs bg-[#009A44] hover:bg-[#007a35] text-white"
          >
            {saving ? (
              <LoaderCircle className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            Save Changes
          </Button>
        </div>
      )}

      {/* ── Grant All Confirmation ───────────────────────────────────── */}
      <AlertDialog open={confirmGrantAll} onOpenChange={setConfirmGrantAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Grant ALL permissions?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedRoleId === "all"
                ? "This will enable every permission for all active roles. This action can be undone by discarding unsaved changes."
                : `This will enable every permission for the selected role. This action can be undone by discarding unsaved changes.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={grantAll} className="bg-emerald-600 hover:bg-emerald-700">
              Grant All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Revoke All Confirmation ──────────────────────────────────── */}
      <AlertDialog open={confirmRevokeAll} onOpenChange={setConfirmRevokeAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke ALL permissions?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedRoleId === "all"
                ? "This will disable every permission for all active roles. This action can be undone by discarding unsaved changes."
                : `This will disable every permission for the selected role. This action can be undone by discarding unsaved changes.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={revokeAll} className="bg-red-600 hover:bg-red-700">
              Revoke All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
